/**
 * Auth store — keeps the access token, refresh token and current user
 * in localStorage so a page reload doesn't lose the session.
 *
 * Refresh-on-401 est câblé côté lib/api.ts : intercepte les 401, appelle
 * POST /auth/refresh avec le refresh_token, met à jour le access via
 * setAccessToken, retry la requête une fois.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserSummary = {
  id: string;
  email: string;
  display_name: string;
  role: string;
  tenant_id: string;
  mfa_enabled: boolean;
  must_change_password: boolean;
};

export type TokenPair = {
  access_token: string;
  refresh_token: string;
  access_expires_at: string;
  refresh_expires_at: string;
  token_type: 'Bearer';
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: UserSummary | null;
  setSession: (user: UserSummary, tokens: TokenPair) => void;
  /** MAJ du seul access token (utilisé après /auth/refresh). */
  setAccessToken: (accessToken: string) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setSession: (user, tokens) =>
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user,
        }),
      setAccessToken: (accessToken) => set({ accessToken }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
    }),
    {
      name: 'adr-m24-auth',
      // Reasonable selector so we don't write the whole store on every change
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);

export const isAuthenticated = (): boolean => {
  return useAuthStore.getState().accessToken !== null;
};
